/** Media vocabulary: request states, channels, failure and waiting reasons (English source). */
import { msg, t } from '@/lib/i18n';
import type { Tone } from '@/ui';

export const CHANNELS: Record<string, string> = {
  forward: msg('Road camera'),
  inward: msg('Cabin camera'),
};

export function channelLabel(ch: string): string {
  const l = CHANNELS[ch];
  return l ? t(l) : ch;
}

/** The request state machine (server: media/models.py TRANSITIONS). */
export const STATUSES: Record<string, { label: string; tone: Tone; description: string }> = {
  created: { label: msg('Created'), tone: 'info', description: msg('The request was just made.') },
  queued: { label: msg('Queued'), tone: 'caution', description: msg('Waiting for its turn on the camera.') },
  sent: { label: msg('Sent'), tone: 'info', description: msg('Asked of the camera; waiting for the upload.') },
  uploading: { label: msg('Uploading'), tone: 'info', description: msg('The camera is uploading the clip.') },
  completed: { label: msg('Completed'), tone: 'good', description: msg('The video is in the library.') },
  failed: { label: msg('Failed'), tone: 'bad', description: msg('The camera could not supply this footage.') },
  expired: { label: msg('Expired'), tone: 'neutral', description: msg('Given up after too many attempts over too long a time.') },
  cancelled: { label: msg('Cancelled'), tone: 'neutral', description: msg('Cancelled before the video arrived.') },
};

export function statusOf(key: string): { label: string; tone: Tone; description: string } {
  return STATUSES[key] ?? { label: key, tone: 'neutral', description: '' };
}

/** The happy path, for the progress stepper. */
export const STEPS = ['created', 'queued', 'sent', 'uploading', 'completed'] as const;

export const FAILURE_REASONS: Record<string, string> = {
  device_offline: msg('Camera offline'),
  timeout: msg('The camera did not answer in time'),
  invalid_timerange: msg('Invalid time range'),
  no_data_available: msg('That footage is no longer on the camera'),
  device_error: msg('Camera error'),
  network_error: msg('Network error'),
  storage_full: msg('Camera storage full'),
  transport_unavailable: msg('No connection to this type of camera in this environment'),
  unknown: msg('Unknown error'),
};

export const WAITING: Record<string, string> = {
  vehicle_offline: msg('Waiting for the vehicle to be switched on: a camera cannot be woken up remotely.'),
  transport_unavailable: msg('No connection to this type of camera in this environment.'),
};

export const END_REASONS: Record<string, string> = {
  manual_cancellation: msg('Cancelled by a person'),
  max_failures_exceeded: msg('Too many failures'),
  request_too_old: msg('Request expired'),
  no_data_available: msg('That footage is no longer on the camera'),
};

export function reasonLabel(map: Record<string, string>, key: string | null | undefined): string | null {
  if (!key) return null;
  const l = map[key];
  return l ? t(l) : key.replace(/_/g, ' ');
}

export const ORIGINS: Record<string, string> = {
  user: msg('Requested by a person'),
  automation: msg('Automatic'),
};

export const QUOTA_TONES: Record<string, Tone> = { ok: 'good', warning: 'caution', exceeded: 'bad', none: 'neutral' };
